'use client'

import dynamic from 'next/dynamic'
import { Container, Grid, Alert } from '@mui/material'

// Types
import type { CardItem } from '@/types/components/molecules/cards'
import type { MapsProps } from '@/types/components/pages/maps'
import type { MarkerItem } from '@/types/components/molecules/map'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Utils
import TransText from '@/utils/trans-text'

// Components
const MapMolecule = dynamic(() => import('@/components/molecules/map'))
const CardMolecule = dynamic(() => import('@/components/molecules/cards'))
const PaginationMolecule = dynamic(() => import('@/components/molecules/pagination'))
const TextAtom = dynamic(() => import('@/components/atoms/text'))

const MapsPage = (props: MapsProps) => {
	// Props
	const { lng, map, articles, limit, page, count } = props

	// Variables
	const { t } = useTranslation(lng)
	const totalPage = Math.ceil(count / limit)

	const cards: CardItem[] = articles.map(article => {
		const fv = article.fields_values ?? []

		return {
			title: article.title,
			image: fv && fv.length > 2 && fv[2].value ? fv[2].value : article.image,
			link: `/article/${article.alias}`,
			year: fv && fv.length > 0 && fv[0].value ? fv[0].value : undefined,
			subtitle: fv && fv.length > 1 && fv[1].value ? fv[1].value : undefined,
			description: fv && fv.length > 5 && fv[5].value ? fv[5].value : undefined
		}
	})

	// Map
	const fv = map?.fields_values ?? []
	let mapKey: string = ''
	let finalDescription = TransText('')
	let markers: MarkerItem[] = []

	if (Array.isArray(fv) && fv.length > 2) {
		finalDescription = TransText(fv[1].value || '')
		const markersJSON = JSON.parse(fv[2].value)
		mapKey = fv[0].value

		if (Array.isArray(markersJSON))
			markers = markersJSON.map(item => {
				return {
					lat: item.Latitude ? parseFloat(item.Latitude) : 0,
					lng: item.Longitude ? parseFloat(item.Longitude) : 0,
					title: item.Title ?? '',
					link: item.Link ?? '',
					image: item.Image ?? ''
				}
			})
	}

	return (
		<Grid container spacing={3}>
			<MapMolecule mapKey={mapKey} markers={markers} />

			<Grid size={12}>
				<Container>
					<Grid id="search" container spacing={3}>
						{finalDescription.text && (
							<Grid size={12}>
								<TextAtom>{finalDescription.text}</TextAtom>
							</Grid>
						)}

						{cards.length ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								{cards.map((card, index) => {
									if (index < 2) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
									else return null
								})}
							</Grid>
						) : null}

						{cards.length > 2 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								{cards.map((card, index) => {
									if (index > 1 && index < 5) return <CardMolecule key={index} lng={lng} {...card} size="small" />
									else return null
								})}
							</Grid>
						) : null}

						{cards.length > 5 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								<CardMolecule lng={lng} {...cards[5]} size="large" />
							</Grid>
						) : null}

						{cards.length > 6 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								{cards.map((card, index) => {
									if (index > 5 && index < 8) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
									else return null
								})}
							</Grid>
						) : null}

						{cards.length > 8 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								<CardMolecule lng={lng} {...cards[8]} size="large" />
							</Grid>
						) : null}

						{cards.length > 9 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								{cards.map((card, index) => {
									if (index > 8 && index < 12) return <CardMolecule key={index} lng={lng} {...card} size="small" />
									else return null
								})}
							</Grid>
						) : null}

						{cards.length > 12 ? (
							<Grid container alignItems="stretch" spacing={2} width={1}>
								{cards.map((card, index) => {
									if (index > 11) return <CardMolecule key={index} lng={lng} {...card} size="medium" />
									else return null
								})}
							</Grid>
						) : null}
					</Grid>
				</Container>
			</Grid>

			{cards.length === 0 ? (
				<Grid size={12}>
					<Container>
						<Alert severity="warning">{t('common:description.notFoundItems')}</Alert>
					</Container>
				</Grid>
			) : null}

			<Grid size={12}>
				<Container>
					{totalPage > 1 && (
						<Grid container justifyContent="center" spacing={2}>
							<PaginationMolecule page={page} count={totalPage} />
						</Grid>
					)}
				</Container>
			</Grid>
		</Grid>
	)
}

export default MapsPage
